import { useState } from 'react';
import { useLanguage } from '@/i18n/LanguageContext';
import { apartments } from '@/data/apartments';
import { motion } from 'framer-motion';
import { Send, CheckCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { contactSchema } from '@/lib/contactSchema';

const ContactForm = () => {
  const { t } = useLanguage();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', apartment: '', dates: '', message: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    const result = contactSchema.safeParse(form);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) fieldErrors[err.path[0] as string] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }
    setLoading(true);
    const { data: insertData, error } = await supabase.from('contact_inquiries').insert({
      name: form.name,
      email: form.email,
      apartment: form.apartment || null,
      dates: form.dates || null,
      message: form.message
    }).select();
    if (!error && insertData?.[0]) {
      // Send auto-confirmation email with inquiry_id for validation
      supabase.functions.invoke('send-contact-confirmation', {
        body: { inquiry_id: insertData[0].id },
      }).catch(console.error);

      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 4000);
      setForm({ name: '', email: '', apartment: '', dates: '', message: '' });
    }
    setLoading(false);
  };

  const inputClass =
  'w-full rounded-sm border border-border/50 bg-secondary/30 px-4 py-3 font-sans text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary/50 focus:outline-none focus:ring-1 focus:ring-primary/30 transition-colors';

  return (
    <section id="contact" className="relative py-24 md:py-32">
      <div className="container mx-auto px-6">
        <div className="grid gap-12 md:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}>

            <p className="font-serif-elegant uppercase tracking-[0.3em] text-primary/70 text-base font-bold">
              Get in Touch
            </p>
            <h2 className="mt-3 font-serif text-3xl tracking-wide text-foreground md:text-4xl">
              {t.contact.title}
            </h2>
            <p className="mt-4 text-base text-muted-foreground">{t.contact.subtitle}</p>

            <div className="mt-12">
              <img
                src="https://images.unsplash.com/photo-1569154941061-e231b4725ef1?w=600&q=80"
                alt="Rome view"
                className="h-64 w-full rounded-lg object-cover opacity-60"
                loading="lazy" />

            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}>

            <form onSubmit={handleSubmit} className="glass-card gold-border space-y-5 rounded-lg p-8">
              <div>
                <label htmlFor="contact-name" className="mb-2 block font-sans text-xs uppercase tracking-widest text-muted-foreground">{t.contact.name}</label>
                <input
                  id="contact-name"
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={inputClass} />
                {errors.name && <p className="mt-1 text-xs text-red-400">{errors.name}</p>}
              </div>
              <div>
                <label htmlFor="contact-email" className="mb-2 block font-sans text-xs uppercase tracking-widest text-muted-foreground">{t.contact.email}</label>
                <input
                  id="contact-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={inputClass} />
                {errors.email && <p className="mt-1 text-xs text-red-400">{errors.email}</p>}
              </div>
              <div>
                <label htmlFor="contact-apartment" className="mb-2 block font-sans text-xs uppercase tracking-widest text-muted-foreground">{t.contact.apartment}</label>
                <select
                  id="contact-apartment"
                  value={form.apartment}
                  onChange={(e) => setForm({ ...form, apartment: e.target.value })}
                  className={inputClass}>

                  <option value="">{t.contact.selectApartment}</option>
                  {apartments.map((a) =>
                  <option key={a.id} value={a.id}>{a.name}</option>
                  )}
                </select>
              </div>
              <div>
                <label htmlFor="contact-dates" className="mb-2 block font-sans text-xs uppercase tracking-widest text-muted-foreground">{t.contact.dates}</label>
                <input
                  id="contact-dates"
                  type="text"
                  value={form.dates}
                  onChange={(e) => setForm({ ...form, dates: e.target.value })}
                  className={inputClass}
                  placeholder="e.g., March 1 - April 15" />

              </div>
              <div>
                <label htmlFor="contact-message" className="mb-2 block font-sans text-xs uppercase tracking-widest text-muted-foreground">{t.contact.message}</label>
                <textarea
                  id="contact-message"
                  rows={4}
                  required
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className={inputClass} />
                {errors.message && <p className="mt-1 text-xs text-red-400">{errors.message}</p>}
              </div>
              <button
                type="submit"
                className="flex w-full items-center justify-center gap-2 rounded-sm bg-primary py-3.5 font-sans text-xs uppercase tracking-[0.2em] text-primary-foreground transition-all duration-300 hover:bg-primary/90">

                {submitted ?
                <>
                    <CheckCircle className="h-4 w-4" />
                    {t.contact.success}
                  </> :

                <>
                    <Send className="h-4 w-4" />
                    {t.contact.send}
                  </>
                }
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>);

};

export default ContactForm;